import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import getApiBaseURL from "../../utils/apiBase";
import { toast, ToastContainer } from "react-toastify";
import SignatureField from "../SignatureField";
import { useAuth } from "../../contexts/AuthContext";
import { getPreviewUrlFromPath } from "../../utils/fileHelpers";
import secureAxios from "../../utils/secureAxios";

const MonthlyReportForm = ({ onSuccess }) => {
  const [consumers, setConsumers] = useState([]);
  const [services, setServices] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consumerSignature, setConsumerSignature] = useState("");
  const [staffSignature, setStaffSignature] = useState("");
  const [otherSignature, setOtherSignature] = useState("");

  const consumerCanvasRef = useRef();
  const staffCanvasRef = useRef();
  const otherCanvasRef = useRef();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const prefillAuthorizationId = searchParams.get("authorization_id");
  const prefillConsumerId = searchParams.get("consumer_id");
  const navigate = useNavigate();
  const todayDate = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    consumer_id: prefillConsumerId || "",
    service_id: "",
    authorization_id: prefillAuthorizationId || "",
    reportName: "West Virginia Department of Commerce Rehabilitation Services",
    reportNumber: "",
    dateOfReport: todayDate,
    estimatedCompletionDate: "",
    goal: "",
    objectives: "",
    results: "",
    productivity: "",
    assessment: "",
    consumerAssessment: "",
    postTest: "",
    recommendations: "",
    consumerSignatureDate: todayDate,
    staffSignatureDate: todayDate,
    otherSignatureDate: todayDate,
    status_id: "", // Add status_id
  });

  const { user, loadingUser, tenant_id: tenantId } = useAuth();
  const userId = user?.user_id || null;

  // Fetch available statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const apiBase = getApiBaseURL();
        // Get all statuses and filter by status_type_id = 1
        const response = await secureAxios.get(`${apiBase}/api/status`);
        const monthlyReportStatuses = response.data.filter(
          (status) => status.status_type_id === 1
        );
        setStatuses(monthlyReportStatuses);

        // Set default status to "Pending" if available
        const pendingStatus = monthlyReportStatuses.find(
          (s) => s.status.toLowerCase() === "pending"
        );
        if (pendingStatus && !formData.status_id) {
          setFormData((prev) => ({
            ...prev,
            status_id: pendingStatus.status_id,
          }));
        }
      } catch (err) {
        toast.error("Unable to load status options.");
      }
    };

    if (tenantId) {
      fetchStatuses();
    }
  }, [tenantId]);

  useEffect(() => {
    const apiBase = getApiBaseURL();
    if (loadingUser || !tenantId || !user?.user_id) return;

    secureAxios
      .get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id: tenantId,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type,
        },
      })
      .then((res) => {
        // Filter to only consumers with "Active" status
        const activeConsumers = res.data.filter(
          (c) => c.status?.toLowerCase() === "active"
        );
        setConsumers(activeConsumers);
      })
      .catch((err) => {
        toast.error("Unable to load consumers.");
        setConsumers([]);
      });
  }, [tenantId, user, loadingUser]);

  useEffect(() => {
    const apiBase = getApiBaseURL();
    if (!tenantId || !formData.consumer_id || !user) return;

    secureAxios
      .get(
        `${apiBase}/api/services?tenant_id=${tenantId}&consumer_id=${formData.consumer_id}`
      )
      .then((res) => setServices(res.data))
      .catch((err) => {
        setServices([]);
      });

    secureAxios
      .get(`${apiBase}/api/auths/consumer/${formData.consumer_id}`, {
        params: { tenant_id: tenantId, auth_type: "service" },
      })
      .then((res) => {
        // Filter to only incomplete authorizations
        const incompleteAuths = res.data.filter(
          (auth) => auth.complete === false || auth.complete === null
        );
        setAuthorizations(incompleteAuths);

        const selected = incompleteAuths.find(
          (auth) => auth.authorization_id === formData.authorization_id
        );
        if (selected) {
          setFormData((prev) => ({ ...prev, service_id: selected.service_id }));
        }
      })
      .catch((err) => {
        setAuthorizations([]);
      });
  }, [formData.consumer_id, tenantId, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearSignature = (ref, setValue) => {
    if (ref?.current?.clear) {
      ref.current.clear();
      setValue("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !userId ||
      !formData.service_id ||
      !formData.consumer_id ||
      !formData.authorization_id
    ) {
      toast.error("Please complete all required fields.");
      setLoading(false);
      return;
    }

    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");
    const randomSuffix = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    const reportNumber = `WV-${formattedDate}-${randomSuffix}`;

    const apiBase = getApiBaseURL();

    try {
      const consumerSignature = consumerCanvasRef.current?.toDataURL?.() || "";
      const staffSignature = staffCanvasRef.current?.toDataURL?.() || "";
      const otherSignature = otherCanvasRef.current?.toDataURL?.() || "";

      const payload = {
        tenant_id: tenantId,
        consumer_id: formData.consumer_id,
        user_id: userId,
        service_id: formData.service_id,
        authorization_id: formData.authorization_id,
        report_name: formData.reportName,
        report_number: reportNumber,
        date_of_report: formData.dateOfReport,
        estimated_completion_date: formData.estimatedCompletionDate,
        goal: formData.goal,
        objectives: formData.objectives,
        results: formData.results,
        productivity: formData.productivity,
        assessment: formData.assessment,
        consumer_assessment: formData.consumerAssessment,
        post_test: formData.postTest,
        recommendations: formData.recommendations,
        consumer_signature_base64: consumerSignature,
        consumer_signature_date: formData.consumerSignatureDate,
        staff_signature_base64: staffSignature,
        staff_signature_date: formData.staffSignatureDate,
        other_signature_base64: otherSignature,
        other_signature_date: formData.otherSignatureDate,
        status_id: formData.status_id, // Include status_id
      };

      // Use secureAxios for the request
      const response = await secureAxios.post(
        `${apiBase}/api/reports/create`,
        payload
      );

      toast.success(
        "Monthly Report created successfully! The report has been securely saved and encrypted."
      );

      const previewUrl = getPreviewUrlFromPath(
        response.data.documentPath,
        apiBase
      );

      // Log audit for viewing the PDF
      try {
        await secureAxios.post(`${apiBase}/api/audit-logs/log`, {
          action: "VIEW_MONTHLY_REPORT_PDF",
          entity_type: "MONTHLY_REPORT",
          entity_id: response.data.reportId,
          entity_identifier: `PDF for Report ${reportNumber}`,
          purpose_of_use: "TREATMENT",
          contains_phi: true,
          phi_types: ["medical_information", "service_records"],
          data_sensitivity_level: "high",
          fields_accessed: ["full_report_pdf"],
        });
      } catch (auditErr) {
        // Silent fail for audit logging
      }

      window.open(previewUrl, "_blank");

      // Clear form
      setFormData((prev) => ({
        ...prev,
        consumer_id: "",
        service_id: "",
        authorization_id: "",
        reportName: prev.reportName,
        reportNumber: "",
        dateOfReport: todayDate,
        estimatedCompletionDate: "",
        goal: "",
        objectives: "",
        results: "",
        productivity: "",
        assessment: "",
        consumerAssessment: "",
        postTest: "",
        recommendations: "",
        consumerSignatureDate: todayDate,
        staffSignatureDate: todayDate,
        otherSignatureDate: todayDate,
        status_id: prev.status_id, // Keep default status
      }));

      setConsumerSignature("");
      setStaffSignature("");
      setOtherSignature("");

      // Clear signature canvases
      handleClearSignature(consumerCanvasRef, setConsumerSignature);
      handleClearSignature(staffCanvasRef, setStaffSignature);
      handleClearSignature(otherCanvasRef, setOtherSignature);

      // Call onSuccess callback which should switch to list tab
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      toast.error("Error creating report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <form onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Monthly Progress Report</h2>
          <span className="badge bg-warning text-dark">
            <i className="bi bi-shield-lock me-1"></i>
            HIPAA Protected Form
          </span>
        </div>

        <div className="alert alert-info mb-4">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Privacy Notice:</strong> This form collects Protected Health
          Information (PHI). All data is encrypted and access is monitored. Only
          enter information for consumers you are authorized to serve.
        </div>

        {/* Consumer Selector */}
        <div className="mb-3">
          <label>
            Consumer <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="consumer_id"
            value={formData.consumer_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Consumer</option>
            {Array.isArray(consumers) &&
              consumers.map((consumer) => (
                <option key={consumer.consumer_id} value={consumer.consumer_id}>
                  {consumer.name}
                </option>
              ))}
          </select>
          {Array.isArray(consumers) && consumers.length === 0 && (
            <p className="text-warning mt-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              No active consumers assigned to you. Only consumers with "Active"
              status can have new reports created.
            </p>
          )}
        </div>

        {/* Authorization Selector */}
        <div className="mb-3">
          <label>
            Authorization Number <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="authorization_id"
            value={formData.authorization_id}
            onChange={(e) => {
              const selectedAuthId = e.target.value;
              const selectedAuth = authorizations.find(
                (auth) => auth.authorization_id === selectedAuthId
              );
              setFormData((prev) => ({
                ...prev,
                authorization_id: selectedAuthId,
                service_id: selectedAuth?.service_id || "",
              }));
            }}
            required
            disabled={!formData.consumer_id}
          >
            <option value="">
              {!formData.consumer_id
                ? "Select a consumer first"
                : "Select Authorization"}
            </option>
            {Array.isArray(authorizations) &&
              authorizations.map((auth) => (
                <option
                  key={auth.authorization_id}
                  value={auth.authorization_id}
                >
                  {auth.authorization_number} —{" "}
                  {auth.service_name || "No Service Name"}
                </option>
              ))}
          </select>
          {formData.consumer_id && authorizations.length === 0 && (
            <p className="text-warning mt-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              No incomplete authorizations available for this consumer. All
              authorizations may be completed.
            </p>
          )}
        </div>

        {/* Dates and Fields */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label>
              Date of Report <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="dateOfReport"
              value={formData.dateOfReport}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label>
              Estimated Completion Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="estimatedCompletionDate"
              value={formData.estimatedCompletionDate}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="alert alert-secondary mb-3">
          <i className="bi bi-pencil-square me-2"></i>
          <strong>Report Content:</strong> Please provide detailed information
          for each section below. This information will be included in the
          official report.
        </div>

        {/* Dynamic Textareas */}
        {[
          "goal",
          "objectives",
          "results",
          "productivity",
          "assessment",
          "consumerAssessment",
          "postTest",
          "recommendations",
        ].map((field) => (
          <div className="mb-3" key={field}>
            <label>
              {field
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
            </label>
            <textarea
              className="form-control"
              name={field}
              value={formData[field]}
              onChange={handleChange}
              rows="3"
              placeholder="Enter confidential information here..."
            />
          </div>
        ))}

        <div className="alert alert-warning mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Electronic Signature Requirements:</strong> By signing below,
          you certify that the information provided is accurate and complete.
          Electronic signatures are legally binding under the E-Sign Act.
        </div>

        {/* Signatures */}
        {[
          {
            label: "Consumer Signature",
            value: consumerSignature,
            setValue: setConsumerSignature,
            refObj: consumerCanvasRef,
            dateField: "consumerSignatureDate",
          },
          {
            label: "Staff Signature",
            value: staffSignature,
            setValue: setStaffSignature,
            refObj: staffCanvasRef,
            dateField: "staffSignatureDate",
          },
          {
            label: "Other Signature (Optional)",
            value: otherSignature,
            setValue: setOtherSignature,
            refObj: otherCanvasRef,
            dateField: "otherSignatureDate",
          },
        ].map(({ label, value, setValue, refObj, dateField }) => (
          <div className="col-md-12 mb-4" key={label}>
            <label className="form-label">{label}</label>
            <div style={{ border: "1px solid #ccc" }}>
              <SignatureField
                ref={refObj}
                value={value}
                penColor="blue"
                onChange={setValue}
              />
            </div>
            <div className="row align-items-center mt-2">
              <div className="col-md-6">
                <label className="form-label">{label} Date</label>
                <input
                  type="date"
                  className="form-control"
                  name={dateField}
                  value={formData[dateField]}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => handleClearSignature(refObj, setValue)}
                >
                  Clear Signature
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Status Selector - Moved to bottom */}
        <div className="mb-4">
          <label>Report Status</label>
          <select
            className="form-select"
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
          >
            <option value="">Select Status</option>
            {Array.isArray(statuses) &&
              statuses.map((status) => (
                <option key={status.status_id} value={status.status_id}>
                  {status.status}
                </option>
              ))}
          </select>
        </div>

        <div className="d-grid mt-4">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>{" "}
                Securely Submitting...
              </>
            ) : (
              <>
                <i className="bi bi-lock-fill me-2"></i>
                Submit Monthly Report Securely
              </>
            )}
          </button>
        </div>

        <div className="text-center text-muted mt-3">
          <small>
            <i className="bi bi-shield-check me-1"></i>
            This form is HIPAA compliant. All data is encrypted in transit and
            at rest.
          </small>
        </div>
      </form>
      <ToastContainer />
    </div>
  );
};

export default MonthlyReportForm;
